import React, {useCallback} from "react";
import {
	Box,
	Button,
	Card,
	Grid,
	IconButton,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import CloseIcon from "@material-ui/icons/Close";
import LayersIcon from "@material-ui/icons/Layers";
import {makeStyles} from "@material-ui/core/styles";
import ItemSelected from "../../ProductsSelection/components/ItemSelected";

const useStyles = makeStyles((theme) => ({
	conditionCard: {
		padding: "20px",
		marginBottom: "16px",
		background: "#f8faf9",
		border: "1px solid #e2e8f0",
		borderRadius: "12px",
	},
	groupHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: "16px",
	},
	groupBadge: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		fontWeight: 600,
		color: "#2d3748",
		fontSize: "1rem",
	},
	conditionRow: {
		padding: "12px",
		marginBottom: "8px",
		background: "#fff",
		borderRadius: "8px",
		border: "1px solid #edf2f7",
	},
	orDivider: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		margin: "8px 0",
	},
	orText: {
		background: "#9cc54b",
		color: "#fff",
		padding: "4px 16px",
		borderRadius: "20px",
		fontWeight: 700,
		fontSize: "0.85rem",
	},
}));

// Fields available for selection criteria (same as landing page / ProductsSelection)
const SELECTION_FIELDS = [
	{field: "Product SKU", options: ["IN", "NOT IN"]},
	{field: "Product ID", options: ["IN", "NOT IN"]},
	{field: "Category", options: ["IN", "NOT IN"]},
	{field: "Subcategory", options: ["IN", "NOT IN"]},
	{field: "Product Name", options: ["starts with", "contains"]},
	{field: "Product Description", options: ["starts with", "contains"]},
	{field: "Price", options: ["bigger than", "less than"]},
	{field: "Supplier", options: ["IN", "NOT IN"]},
	{field: "Brand", options: ["IN", "NOT IN"]},
	{field: "Country", options: ["IN", "NOT IN"]},
	{field: "Dietary", options: ["IN", "NOT IN"]},
	{field: "Discount", options: ["YES", "NO"]},
];

export const isTypeMultiSelect = (type) => {
	return [
		"Product SKU",
		"Product ID",
		"Category",
		"Subcategory",
		"Supplier",
		"Brand",
		"Country",
		"Dietary",
	].includes(type);
};

export const isTypeBoolean = (type) => {
	return ["Discount"].includes(type);
};

export const fieldToTargetTable = (field) => {
	switch (field) {
		case "Product SKU": return "list_products";
		case "Product ID": return "list_products";
		case "Category": return "list_categories";
		case "Subcategory": return "list_sub_categories";
		case "Supplier": return "suppliers";
		case "Brand": return "list_brands";
		case "Country": return "countries";
		case "Dietary": return "dietary";
		case "Product Name": return "product_name";
		case "Product Description": return "product_description";
		case "Price": return "price";
		case "Discount": return "discount";
		default: return "list_categories";
	}
};

export const targetTableToField = (table) => {
	if (!table) return "Category";
	const t = table.toLowerCase();
	if (t === "list_products" || t === "store_item_infos") return "Product SKU";
	if (t === "list_sub_categories" || t.includes("sub_cat") || t.includes("subcategor")) return "Subcategory";
	if (t === "list_categories" || t.includes("categor")) return "Category";
	if (t === "suppliers") return "Supplier";
	if (t === "list_brands" || t === "brands") return "Brand";
	if (t === "countries") return "Country";
	if (t === "dietary") return "Dietary";
	if (t === "product_name") return "Product Name";
	if (t === "product_description") return "Product Description";
	if (t === "price") return "Price";
	if (t === "discount") return "Discount";
	return "Category";
};

let groupIdCounter = 1;
export const generateGroupId = () => `group_${Date.now()}_${groupIdCounter++}`;

export const createEmptyCondition = () => ({
	field: "",
	option: "",
	value: [],
});

export const createEmptyGroup = () => ({
	id: generateGroupId(),
	conditions: [createEmptyCondition()],
});

/**
 * Extract label, id, and imageUrl from a resolved_target object based on criterial_type.
 *
 * Each field type uses different property names in the resolved object:
 *   Category      → { cat_id, name, image }
 *   Subcategory   → { sub_cat_id, name, image }
 *   Brand         → { brand_id, name, image }
 *   Supplier      → { id, first_name, supplier_logo }
 *   Country       → { id, name, code (used as flag icon) }
 *   Dietary       → { diet_id, name, image_url }
 *   Product SKU   → { product_code, name, default_image_url }
 *   Product / Product ID → { item_id, name, default_image_url }
 */
const resolveTargetItem = (field, resolved, selectionValue) => {
	if (!resolved) {
		// Fallback: no resolved_target available, use raw selection_value
		return {label: selectionValue, value: selectionValue, id: selectionValue};
	}
	switch (field) {
		case "Category":
			return {label: resolved.name, value: resolved.name, id: resolved.cat_id, imageUrl: resolved.image};
		case "Subcategory":
			return {label: resolved.name, value: resolved.name, id: resolved.sub_cat_id, imageUrl: resolved.image};
		case "Brand":
			return {label: resolved.name, value: resolved.name, id: resolved.brand_id, imageUrl: resolved.image};
		case "Supplier":
			return {label: resolved.first_name, value: resolved.first_name, id: resolved.id, imageUrl: resolved.supplier_logo};
		case "Country":
			return {label: resolved.name, value: resolved.name, id: resolved.id, imageUrl: resolved.code};
		case "Dietary":
			return {label: resolved.name, value: resolved.name, id: resolved.diet_id, imageUrl: resolved.image_url};
		case "Product SKU":
			return {label: resolved.name, value: resolved.name, id: resolved.product_code, imageUrl: resolved.default_image_url};
		case "Product":
		case "Product ID":
			return {label: resolved.name, value: resolved.name, id: resolved.item_id, imageUrl: resolved.resized_image_url || resolved.default_image_url};
		default:
			return {label: resolved.name || selectionValue, value: resolved.name || selectionValue, id: selectionValue};
	}
};

/**
 * Build groups from backend targets array for edit mode hydration.
 *
 * Backend response targets contain:
 *   criterial_type ("Category", "Brand", etc.), selection_value (string ID),
 *   condition ("IN", "NOT IN", etc.), group_id,
 *   resolved_target: { cat_id, name, image, ... } (the resolved record from the related table)
 */
export const buildGroupsFromTargets = (targets) => {
	if (!targets || targets.length === 0) return [createEmptyGroup()];

	const groupMap = {};
	targets.forEach((t) => {
		const gid = t.group_id || "default";
		if (!groupMap[gid]) groupMap[gid] = [];
		groupMap[gid].push(t);
	});

	const result = [];
	Object.entries(groupMap).forEach(([gid, items]) => {
		const conditionMap = {};
		items.forEach((t) => {
			const field = t.criterial_type || t.field;
			const option = t.condition || "IN";
			const key = `${field}_${option}`;
			if (!conditionMap[key]) {
				conditionMap[key] = {field, option, value: []};
			}

			// For multi-select fields, build value objects from resolved_target
			if (isTypeMultiSelect(field)) {
				const selValue = t.selection_value;
				if (selValue != null) {
					conditionMap[key].value.push(
						resolveTargetItem(field, t.resolved_target, selValue)
					);
				}
			}
			// For text/number fields, store raw value
			else if (!isTypeBoolean(field)) {
				const val = t.selection_value ?? "";
				conditionMap[key].value = val;
			}
			// For boolean fields, value stays empty
		});
		result.push({
			id: generateGroupId(),
			conditions: Object.values(conditionMap).length > 0
				? Object.values(conditionMap)
				: [createEmptyCondition()],
		});
	});

	return result.length > 0 ? result : [createEmptyGroup()];
};

/**
 * Flatten groups into targets array + selection_groups for the backend payload.
 *
 * Backend expects display names for criterial_type and field:
 *   targets[]: { criterial_type ("Category", "Brand", etc.), selection_value (string|null), condition, group_id }
 *   selection_groups[]: { group_id, conditions[]: { field ("Category", etc.), condition, value (string[]) } }
 */
export const buildPayloadFromGroups = (groups) => {
	const selectionGroups = groups.map((group, groupIndex) => {
		const conditions = group.conditions
			.filter((c) => c.field && c.option && (isTypeMultiSelect(c.field) ? c.value?.length > 0 : (isTypeBoolean(c.field) || (c.value !== "" && c.value !== undefined))))
			.map((c) => ({
				field: c.field,
				condition: c.option,
				value: isTypeMultiSelect(c.field)
					? (c.value || []).map((v) => String(v.id))
					: isTypeBoolean(c.field)
						? [null]
						: [String(c.value)],
			}));
		return {
			group_id: groupIndex + 1,
			conditions,
		};
	}).filter((g) => g.conditions.length > 0);

	const targets = [];
	selectionGroups.forEach((group) => {
		group.conditions.forEach((condition) => {
			if (isTypeBoolean(condition.field)) {
				targets.push({
					criterial_type: condition.field,
					selection_value: null,
					condition: condition.condition,
					group_id: group.group_id,
				});
			} else {
				(condition.value || []).forEach((val) => {
					targets.push({
						criterial_type: condition.field,
						selection_value: val != null ? String(val) : null,
						condition: condition.condition,
						group_id: group.group_id,
					});
				});
			}
		});
	});

	return {targets, selection_groups: selectionGroups};
};

/**
 * SelectionCriteria component
 *
 * A reusable group-based selection criteria UI (same pattern as ProductsSelection).
 *
 * Props:
 *   groups       - array of group objects [{id, conditions: [{field, option, value}]}]
 *   setGroups    - state setter for groups
 *   title        - optional section title (default: "Selection Criteria")
 *   description  - optional helper text
 */
export default function SelectionCriteria({
	groups,
	setGroups,
	title = "Selection Criteria",
	description = "Define groups of conditions to target specific categories, subcategories, or products. Conditions within a group use AND logic. Multiple groups use OR logic. Leave empty to apply to all.",
}) {
	const classes = useStyles();

	// --- Group / Condition operations ---
	const addGroup = useCallback(() => {
		setGroups((prev) => [...prev, createEmptyGroup()]);
	}, [setGroups]);

	const removeGroup = useCallback((groupId) => {
		setGroups((prev) => prev.filter((g) => g.id !== groupId));
	}, [setGroups]);

	const addCondition = useCallback((groupId) => {
		setGroups((prev) =>
			prev.map((g) =>
				g.id === groupId
					? {...g, conditions: [...g.conditions, createEmptyCondition()]}
					: g
			)
		);
	}, [setGroups]);

	const removeCondition = useCallback((groupId, conditionIndex) => {
		setGroups((prev) =>
			prev.map((g) =>
				g.id === groupId
					? {...g, conditions: g.conditions.filter((_, i) => i !== conditionIndex)}
					: g
			)
		);
	}, [setGroups]);

	const updateCondition = useCallback((groupId, conditionIndex, key, value) => {
		setGroups((prev) =>
			prev.map((g) => {
				if (g.id !== groupId) return g;
				const newConditions = [...g.conditions];
				newConditions[conditionIndex] = {
					...newConditions[conditionIndex],
					[key]: value,
					...(key === "field" ? {value: [], option: ""} : {}),
				};
				return {...g, conditions: newConditions};
			})
		);
	}, [setGroups]);

	const handleMultiSelectChange = useCallback((groupId, conditionIndex, field, {selectedList}) => {
		setGroups((prev) =>
			prev.map((g) => {
				if (g.id !== groupId) return g;
				const newConditions = [...g.conditions];
				newConditions[conditionIndex] = {
					...newConditions[conditionIndex],
					value: selectedList || [],
				};
				return {...g, conditions: newConditions};
			})
		);
	}, [setGroups]);

	const formatValuesForMultiSelect = useCallback((values) => {
		if (!values || !Array.isArray(values)) return [];
		return values;
	}, []);

	return (
		<Box>
			{title && (
				<Typography variant="subtitle2" style={{fontWeight: 600, marginBottom: 4, color: "#555"}}>
					{title}
				</Typography>
			)}
			{description && (
				<Typography variant="caption" color="textSecondary" style={{display: "block", marginBottom: 12}}>
					{description}
				</Typography>
			)}

			{groups.map((group, groupIndex) => (
				<Box key={group.id}>
					<Card className={classes.conditionCard}>
						<Box className={classes.groupHeader}>
							<Box className={classes.groupBadge}>
								<LayersIcon/>
								Group {groupIndex + 1}
							</Box>
							{groups.length > 1 && (
								<Tooltip title="Remove this group">
									<IconButton
										onClick={() => removeGroup(group.id)}
										style={{color: "#e53e3e"}}
										size="small"
									>
										<CloseIcon/>
									</IconButton>
								</Tooltip>
							)}
						</Box>

						{group.conditions.map((condition, conditionIndex) => {
							const usedFields = group.conditions
								.filter((_, idx) => idx !== conditionIndex)
								.map((c) => c.field);

							return (
								<Box key={conditionIndex} className={classes.conditionRow}>
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={12} md={3}>
											<Select
												fullWidth
												variant="outlined"
												value={condition.field}
												onChange={(e) =>
													updateCondition(group.id, conditionIndex, "field", e.target.value)
												}
												displayEmpty
												style={{minHeight: "55px"}}
											>
												<MenuItem value="" disabled>
													Select Field
												</MenuItem>
												{SELECTION_FIELDS.map((f, idx) => (
													<MenuItem
														key={idx}
														disabled={usedFields.includes(f.field) && f.field !== condition.field}
														value={f.field}
													>
														{f.field}
													</MenuItem>
												))}
											</Select>
										</Grid>

										<Grid item xs={12} md={2}>
											<Select
												fullWidth
												variant="outlined"
												value={condition.option}
												onChange={(e) =>
													updateCondition(group.id, conditionIndex, "option", e.target.value)
												}
												displayEmpty
												disabled={!condition.field}
												style={{minHeight: "55px"}}
											>
												<MenuItem value="" disabled>
													Operator
												</MenuItem>
												{SELECTION_FIELDS
													.find((f) => f.field === condition.field)
													?.options?.map((opt, idx) => (
														<MenuItem key={idx} value={opt}>
															{opt}
														</MenuItem>
													))}
											</Select>
										</Grid>

										<Grid item xs={12} md={6}>
											{condition.field ? (
												isTypeMultiSelect(condition.field) ? (
													<ItemSelected
														fieldType={condition.field}
														selecte={formatValuesForMultiSelect(condition.value)}
														onchange={(blockId, condIdx, field, data) =>
															handleMultiSelectChange(blockId, condIdx, field, data)
														}
														onRemove={(blockId, condIdx, field, data) =>
															handleMultiSelectChange(blockId, condIdx, field, data)
														}
														item={{block: group.id, conditionIndex}}
														placeholder={`Search ${condition.field}...`}
														blockType="conditions"
														singleSelect={false}
														menuPortalTarget={document.body}
														menuPosition="fixed"
													/>
												) : !isTypeBoolean(condition.field) ? (
													<TextField
														fullWidth
														variant="outlined"
														label={condition.field}
														value={condition.value}
														onChange={(e) =>
															updateCondition(group.id, conditionIndex, "value", e.target.value)
														}
														style={{minHeight: "55px"}}
													/>
												) : null
											) : (
												<TextField
													fullWidth
													variant="outlined"
													disabled
													placeholder="Select a field first"
													style={{minHeight: "55px"}}
												/>
											)}
										</Grid>

										<Grid item xs={12} md={1}>
											{group.conditions.length > 1 && (
												<Tooltip title="Remove condition">
													<IconButton
														onClick={() => removeCondition(group.id, conditionIndex)}
														style={{color: "#e53e3e"}}
														size="small"
													>
														<RemoveIcon/>
													</IconButton>
												</Tooltip>
											)}
										</Grid>
									</Grid>
								</Box>
							);
						})}

						<Button
							startIcon={<AddIcon/>}
							onClick={() => addCondition(group.id)}
							style={{color: "#9cc54b", fontWeight: 600, marginTop: 8}}
							size="small"
						>
							Add Condition
						</Button>
					</Card>

					{groupIndex < groups.length - 1 && (
						<Box className={classes.orDivider}>
							<Box className={classes.orText}>OR</Box>
						</Box>
					)}
				</Box>
			))}

			<Box display="flex" justifyContent="center" mt={2} mb={1}>
				<Button
					startIcon={<AddIcon/>}
					onClick={addGroup}
					variant="outlined"
					style={{
						color: "#9cc54b",
						borderColor: "#9cc54b",
						fontWeight: 600,
						borderRadius: "8px",
					}}
				>
					Add New Group
				</Button>
			</Box>
		</Box>
	);
}
